import React from 'react';
import { Card, Flex, Statistic, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { DeliveryOutcome, DeliverySummary, successRate, sumCounts } from '../../../domain/entities/DeliveryDashboard';
import { OUTCOME_ICONS } from './deliveryOutcomeStyles';

interface DeliveryKpisProps {
  summary?: DeliverySummary;
  rangeDays: number;
  onOutcomeClick: (outcomes: DeliveryOutcome[]) => void;
}

const Kpi: React.FC<{ title: string; value: number | string; outcomes?: DeliveryOutcome[]; icon?: React.ReactNode; onClick?: (outcomes: DeliveryOutcome[]) => void }> = ({ title, value, outcomes, icon, onClick }) => (
  <button
    type="button"
    className="delivery-kpi"
    disabled={!outcomes || !onClick}
    onClick={() => outcomes && onClick?.(outcomes)}
  >
    <Statistic title={title} value={value} prefix={icon} />
  </button>
);

const DeliveryKpis: React.FC<DeliveryKpisProps> = ({ summary, rangeDays, onOutcomeClick }) => {
  const { t } = useTranslation();
  const past = summary?.past ?? {};
  const future = summary?.future ?? {};
  const rate = successRate(past);

  return (
    <Flex gap={16} wrap="wrap">
      <Card size="small" className="delivery-kpi-card" title={t('delivery-kpi-past', { ns: 'labels', count: rangeDays })}>
        <Flex gap={8} wrap="wrap">
          <Kpi title={t('delivery-kpi-sent', { ns: 'labels' })} value={past.sent ?? 0} outcomes={['sent']} icon={OUTCOME_ICONS.sent} onClick={onOutcomeClick} />
          <Kpi title={t('delivery-kpi-errors', { ns: 'labels' })} value={sumCounts(past, ['failed', 'rejected'])} outcomes={['failed', 'rejected']} icon={OUTCOME_ICONS.failed} onClick={onOutcomeClick} />
          <Kpi title={t('delivery-kpi-retrying', { ns: 'labels' })} value={past.retrying ?? 0} outcomes={['retrying']} icon={OUTCOME_ICONS.retrying} onClick={onOutcomeClick} />
          <Kpi title={t('delivery-kpi-cancelled', { ns: 'labels' })} value={sumCounts(past, ['cancelled', 'unknown'])} outcomes={['cancelled', 'unknown']} icon={OUTCOME_ICONS.cancelled} onClick={onOutcomeClick} />
          <Kpi title={t('delivery-kpi-success-rate', { ns: 'labels' })} value={rate === undefined ? '—' : `${rate}%`} />
        </Flex>
      </Card>
      <Card size="small" className="delivery-kpi-card" title={t('delivery-kpi-future', { ns: 'labels', count: rangeDays })}>
        <Flex gap={8} wrap="wrap">
          <Kpi title={t('delivery-kpi-planned', { ns: 'labels' })} value={sumCounts(future)} outcomes={['planned']} icon={OUTCOME_ICONS.planned} onClick={onOutcomeClick} />
          <Kpi title={t('delivery-kpi-delayed', { ns: 'labels' })} value={past.delayed ?? 0} outcomes={['delayed']} icon={OUTCOME_ICONS.delayed} onClick={onOutcomeClick} />
          <Kpi title={t('delivery-kpi-running', { ns: 'labels' })} value={past.running ?? 0} outcomes={['running']} icon={OUTCOME_ICONS.running} onClick={onOutcomeClick} />
        </Flex>
        {summary && summary.topErrors.length > 0 && (
          <Typography.Text type="secondary" className="delivery-kpi-hint">
            {t('delivery-kpi-top-error', { ns: 'labels', code: summary.topErrors[0].code, count: summary.topErrors[0].count })}
          </Typography.Text>
        )}
      </Card>
    </Flex>
  );
};

export default DeliveryKpis;
